import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { SearchField, Spinner } from '@forwardflow/ui'
import { Lead } from '@forwardflow/services/LeadService'
import { useGetRecentConversations } from '@forwardflow/hooks/customHooks/conversation/useGetRecentConversations'
import { PaginationQueryParams } from '@forwardflow/types'
import { useNavigate, useParams } from 'react-router-dom'
import { LeadListItem } from './LeadListItem'
import { useConversationSSE } from '@forwardflow/hooks/customHooks/conversation/useConversationSSE'
import { debounce } from 'lodash'
import { getStorageKey } from '@forwardflow/helpers/utils'

interface LeadListProps {
   onSelectLead: (leadId: number | null) => void
   selectedStageFilter: string
   selectedCampaignId: string | null
   selectedCampaignName: string | null
}

export const LeadList: React.FC<LeadListProps> = ({
   onSelectLead,
   selectedStageFilter,
   selectedCampaignId,
   selectedCampaignName,
}) => {
   const { id: leadId } = useParams()
   const navigate = useNavigate()
   const listRef = useRef<HTMLDivElement>(null)
   const [accumulatedLeads, setAccumulatedLeads] = useState([] as Lead[])

   const [searchInput, setSearchInput] = useState('')
   const [debouncedSearchTerm, setDebouncedSearchTerm] = useState('')
   const [page, setPage] = useState(1)
   const [dataSourceChanged, setDataSourceChanged] = useState(false)
   const [dataSourceChecked, setDataSourceChecked] = useState(false)
   const [initialLoad, setInitialLoad] = useState(true)
   const LEADS_LIMIT = 15

   console.log('Selected Campaign Id', selectedCampaignId)

   const queryParams: PaginationQueryParams = useMemo(
      () => ({
         limit: LEADS_LIMIT,
         page,
         search: debouncedSearchTerm.trim() || undefined,
         stage:
            selectedStageFilter !== 'all-stages'
               ? selectedStageFilter.toLowerCase()
               : undefined,
         campaignId:
            selectedCampaignId === 'null' ? undefined : selectedCampaignId,
      }),
      [page, debouncedSearchTerm, selectedStageFilter, selectedCampaignId]
   )

   const { leads, loading, error, hasMore, mutate } =
      useGetRecentConversations(queryParams)

   useConversationSSE(mutate)

   // Helper function to build URL with search params
   const buildUrlWithParams = (leadId?: number) => {
      const isDemo = window.location.pathname.includes('demo-conversations-old')
      const basePath = isDemo ? '/demo-conversations-old' : '/conversations'

      const params = new URLSearchParams()

      // Add stage filter if not default
      if (selectedStageFilter !== 'all-stages') {
         let stageParam = 'all-stages'
         if (selectedStageFilter === 'ff-appointment-set') {
            stageParam = 'apt-set'
         } else if (selectedStageFilter === 'ff-appointment-missed') {
            stageParam = 'apt-missed'
         }
         if (stageParam !== 'all-stages') {
            params.set('stages', stageParam)
         }
      }

      // Add campaign filter if exists
      if (selectedCampaignName) {
         params.set('filter', selectedCampaignName)
      }

      const queryString = params.toString()
      const urlSuffix = queryString ? `?${queryString}` : ''

      if (leadId) {
         return `${basePath}/${leadId}${urlSuffix}`
      }
      return `${basePath}${urlSuffix}`
   }

   useEffect(() => {
      setPage(1)
      setAccumulatedLeads([])
      setDataSourceChanged(true)
      setDataSourceChecked(false)
      setInitialLoad(true)
   }, [selectedCampaignId, selectedStageFilter, debouncedSearchTerm])

   useEffect(() => {
      if (dataSourceChanged && !loading) {
         if (leads?.length > 0) {
            const urlLeadExists =
               leadId && leads.some((lead) => lead.leadId === Number(leadId))
            const storageKey = getStorageKey(
               selectedStageFilter,
               selectedCampaignName
            )
            const storedLeadId = localStorage.getItem(storageKey)
            const storedLeadExists =
               storedLeadId &&
               leads.some((lead) => lead.leadId === Number(storedLeadId))

            if (urlLeadExists) {
               onSelectLead(Number(leadId))
            } else if (!leadId) {
               if (storedLeadExists) {
                  navigate(buildUrlWithParams(Number(storedLeadId)), {
                     replace: true,
                  })
                  onSelectLead(Number(storedLeadId))
               } else {
                  navigate(buildUrlWithParams(leads[0].leadId))
                  onSelectLead(leads[0].leadId)
               }
            }
         } else {
            navigate(buildUrlWithParams())
            onSelectLead(null)
         }
         setDataSourceChecked(true)
         setDataSourceChanged(false)
      }

      if (leads?.length) {
         if (page === 1) {
            setAccumulatedLeads(leads)
         } else {
            setAccumulatedLeads((prev) => {
               const newUniqueLeads = leads.filter(
                  (newLead) =>
                     !prev.some(
                        (existingLead) => existingLead.leadId === newLead.leadId
                     )
               )
               return [...prev, ...newUniqueLeads]
            })
         }
      }

      if (!loading) {
         setInitialLoad(false)
      }
   }, [leads, loading, page, dataSourceChanged, navigate, onSelectLead, leadId])

   useEffect(() => {
      if (!leadId) {
         const storageKey = getStorageKey(
            selectedStageFilter,
            selectedCampaignName
         )
         const storedLeadId = localStorage.getItem(storageKey)

         if (storedLeadId) {
            navigate(buildUrlWithParams(Number(storedLeadId)), {
               replace: true,
            })
         }
      }
   }, [leadId, selectedStageFilter, selectedCampaignName, navigate])

   useEffect(() => {
      if (
         leads?.length > 0 &&
         !leadId &&
         !dataSourceChanged &&
         dataSourceChecked
      ) {
         navigate(buildUrlWithParams(leads[0].leadId))
         onSelectLead(leads[0].leadId)
      }
   }, [
      leads,
      leadId,
      dataSourceChanged,
      dataSourceChecked,
      navigate,
      onSelectLead,
   ])

   const debouncedSetSearch = useCallback(
      debounce((value: string) => {
         setDebouncedSearchTerm(value)
         setPage(1)
         setAccumulatedLeads([])
      }, 500),
      []
   )

   const handleSearchChange = useCallback(
      (value: string) => {
         setSearchInput(value)
         debouncedSetSearch(value)
      },
      [debouncedSetSearch]
   )

   useEffect(() => {
      return () => {
         debouncedSetSearch.cancel()
      }
   }, [debouncedSetSearch])

   const handleLeadSelect = useCallback(
      (lead: Lead) => {
         const storageKey = getStorageKey(
            selectedStageFilter,
            selectedCampaignName
         )
         localStorage.setItem(storageKey, lead.leadId.toString())

         navigate(buildUrlWithParams(lead.leadId))
         onSelectLead(lead.leadId)
      },
      [navigate, onSelectLead, selectedStageFilter, selectedCampaignName]
   )

   const SCROLL_THRESHOLD = 200

   const handleScroll = useCallback(() => {
      if (!listRef.current || loading || !hasMore) return

      const { scrollTop, scrollHeight, clientHeight } = listRef.current
      const distanceFromBottom = scrollHeight - scrollTop - clientHeight

      if (distanceFromBottom < SCROLL_THRESHOLD && distanceFromBottom > 0) {
         setPage((prev) => prev + 1)
      }
   }, [loading, hasMore])

   const stripHtml = (html: string) => {
      const tmp = document.createElement('DIV')
      tmp.innerHTML = html
      return tmp.textContent || tmp.innerText || ''
   }

   if (error) return <div>Error: {error}</div>

   const shouldShowSpinner = loading && (initialLoad || page === 1)

   return (
      <div className="flex flex-col w-full lg:max-w-[300px] 2xl:max-w-[420px] max-h-[400px] lg:max-h-full">
         <SearchField
            className="flex py-4 mb-4 border rounded-lg shrink-0 border-primary/40 bg-primary/10"
            placeholder="Search Contacts"
            value={searchInput}
            onChange={handleSearchChange}
         />

         <div
            ref={listRef}
            onScroll={handleScroll}
            className="flex-grow overflow-y-auto border rounded-lg md:w-full bg-primary/10 border-white/10"
         >
            <div className="flex px-8 pt-5 pb-6 font-medium text-white/70">
               Recent Conversations
            </div>

            <div className="flex flex-col gap-[2px] px-3 pb-4">
               {accumulatedLeads?.length > 0 &&
                  accumulatedLeads.map((lead: Lead, index) => (
                     <LeadListItem
                        key={`${lead.leadId}-${index}`}
                        lead={lead}
                        isSelected={lead.leadId === Number(leadId)}
                        onSelect={handleLeadSelect}
                        stripHtml={stripHtml}
                     />
                  ))}

               {!loading &&
                  (!accumulatedLeads || accumulatedLeads.length === 0) && (
                     <div className="flex justify-center p-4 text-white/70">
                        No conversation found.
                     </div>
                  )}

               {shouldShowSpinner && (
                  <div className="flex items-center justify-center py-8">
                     <Spinner />
                  </div>
               )}
               {loading && !initialLoad && page > 1 && (
                  <div className="flex items-center justify-center py-4">
                     <Spinner />
                  </div>
               )}
            </div>
         </div>
      </div>
   )
}
